import { useState } from 'react';

interface Step {
  id: number;
  title: string;
  description: string;
  icon: string;
  command?: string;
  tip?: string;
}

const steps: Step[] = [
  {
    id: 1,
    title: 'Servidor VPS',
    description: 'Tenha um servidor Ubuntu 22.04+ com acesso root. Recomendado mínimo 2 vCPU, 4GB RAM e 40GB SSD.',
    icon: 'ri-server-line',
    tip: 'Hetzner, DigitalOcean, Vultr ou AWS Lightsail são boas opções a partir de R$ 100/mês.',
  },
  {
    id: 2,
    title: 'Docker e Docker Compose',
    description: 'Instale o Docker Engine e Docker Compose para gerenciar os containers.',
    icon: 'ri-box-3-line',
    command: 'curl -fsSL https://get.docker.com | sh && sudo usermod -aG docker $USER',
    tip: 'Após instalar, faça logout/login ou execute: newgrp docker',
  },
  {
    id: 3,
    title: 'Evolution API',
    description: 'Execute a Evolution API para gerenciar as conexões WhatsApp.',
    icon: 'ri-rocket-line',
    command: `docker run -d --name evolution \\
  -p 8080:8080 \\
  -e AUTHENTICATION_API_KEY=sua-chave-aqui \\
  -e AUTHENTICATION_TYPE=apikey \\
  -e DEL_INSTANCE=true \\
  -e QRCODE_LIMIT=60 \\
  --restart unless-stopped \\
  atendai/evolution-api`,
    tip: 'Guarde a API Key em local seguro. Ela será usada no painel WZAP Pro.',
  },
  {
    id: 4,
    title: 'SSL / HTTPS',
    description: 'Configure certificado SSL com Certbot + Nginx para acesso seguro ao seu domínio.',
    icon: 'ri-shield-check-line',
    command: `sudo apt install -y nginx certbot python3-certbot-nginx
sudo certbot --nginx -d api.seudominio.com`,
    tip: 'Configure o DNS do seu domínio antes (registro A apontando pro IP do VPS).',
  },
  {
    id: 5,
    title: 'Domínio e DNS',
    description: 'Aponte seu domínio para o IP do servidor. Configure registros A e CNAME conforme necessário.',
    icon: 'ri-global-line',
    tip: 'Use Cloudflare para gerenciar DNS — é grátis e oferece proteção DDoS básica.',
  },
  {
    id: 6,
    title: 'Webhook',
    description: 'Configure o webhook no painel WZAP Pro para receber atualizações de status em tempo real.',
    icon: 'ri-webhook-line',
    command: `No painel WZAP Pro > Configurações > WhatsApp:
- URL do Servidor: https://api.seudominio.com:8080
- API Key: sua-chave-definida-no-docker`,
    tip: 'Certifique-se que a porta 8080 está liberada no firewall do VPS e na VPC.',
  },
];

export default function DeployPage() {
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [copiedCmd, setCopiedCmd] = useState<number | null>(null);

  const toggleStep = (id: number) => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const copyCommand = async (text: string, id: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedCmd(id);
    setTimeout(() => setCopiedCmd(null), 2000);
  };

  const allDone = completedSteps.size === steps.length;
  const progress = Math.round((completedSteps.size / steps.length) * 100);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-heading font-bold text-foreground-950">Assistente de Implantação</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Siga os passos abaixo para implantar o WZAP Pro no seu próprio servidor. Marque cada etapa como concluída.
        </p>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl border border-background-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-foreground-700">Progresso</span>
          <span className="text-sm font-bold text-foreground-950">{progress}%</span>
        </div>
        <div className="w-full h-2.5 bg-background-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-foreground-400">{completedSteps.size} de {steps.length} concluído{steps.length !== 1 ? 's' : ''}</span>
          {allDone && (
            <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
              <i className="ri-check-double-line" /> Tudo pronto!
            </span>
          )}
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step) => {
          const isDone = completedSteps.has(step.id);
          const isActive = activeStep === step.id;

          return (
            <div
              key={step.id}
              className={`bg-white rounded-xl border transition-all ${
                isDone
                  ? 'border-emerald-200 bg-emerald-50/30'
                  : isActive
                  ? 'border-primary-300'
                  : 'border-background-200'
              }`}
            >
              <button
                type="button"
                onClick={() => setActiveStep(isActive ? null : step.id)}
                className="w-full p-5 flex items-start gap-4 text-left cursor-pointer"
              >
                {/* Step number */}
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                    isDone
                      ? 'bg-emerald-500 text-white'
                      : isActive
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-500'
                  }`}
                >
                  {isDone ? (
                    <i className="ri-check-line text-lg" />
                  ) : (
                    <span className="text-sm font-bold">{step.id}</span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className={`text-sm font-semibold ${isDone ? 'text-emerald-700' : 'text-foreground-900'}`}>
                      {step.title}
                    </h4>
                    {isDone && (
                      <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full whitespace-nowrap">
                        OK
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{step.description}</p>

                  {/* Expanded content */}
                  {isActive && (
                    <div className="mt-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                      {step.command && (
                        <div className="bg-background-50 rounded-lg p-4 relative group">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Comando</span>
                            <button
                              type="button"
                              onClick={() => copyCommand(step.command!, step.id)}
                              className="text-xs text-foreground-400 hover:text-primary-600 flex items-center gap-1 cursor-pointer transition-colors"
                            >
                              <i className={`text-sm ${copiedCmd === step.id ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'}`} />
                              {copiedCmd === step.id ? 'Copiado!' : 'Copiar'}
                            </button>
                          </div>
                          <pre className="text-xs font-mono text-foreground-800 whitespace-pre-wrap break-all leading-relaxed">
                            {step.command}
                          </pre>
                        </div>
                      )}
                      {step.tip && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <i className="ri-lightbulb-line text-amber-500 text-sm flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700">{step.tip}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-3 pt-1">
                        <button
                          type="button"
                          onClick={() => toggleStep(step.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                            isDone
                              ? 'bg-background-100 text-foreground-500 hover:bg-background-200'
                              : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          }`}
                        >
                          {isDone ? (
                            <span className="flex items-center gap-1.5">
                              <i className="ri-arrow-go-back-line" /> Desmarcar
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <i className="ri-check-line" /> Marcar como Concluído
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Expand/collapse arrow */}
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  <i
                    className={`ri-arrow-down-s-line text-sm text-foreground-400 transition-transform ${
                      isActive ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Final message */}
      {allDone && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-check-double-line text-2xl text-emerald-600" />
          </div>
          <h4 className="text-base font-heading font-bold text-emerald-800 mb-1">Implantação Concluída!</h4>
          <p className="text-sm text-emerald-600">
            Todos os passos foram marcados como concluídos. Seu WZAP Pro está pronto para uso no seu próprio servidor.
          </p>
        </div>
      )}

      {/* Info: NUNCA atualizar automaticamente */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-error-warning-line text-red-500" />
        </div>
        <div>
          <p className="text-sm font-semibold text-red-700 mb-0.5">Importante: Nunca usar atualização automática</p>
          <p className="text-xs text-red-600 leading-relaxed">
            Não utilize Watchtower, unattended-upgrades ou qualquer ferramenta de atualização automática para Docker,
            Evolution API, PostgreSQL, Redis ou Nginx. Atualizações devem ser feitas manualmente e com backup prévio.
            Uma atualização incompatível pode causar indisponibilidade total do sistema.
          </p>
        </div>
      </div>
    </div>
  );
}